import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { AREA_VALUES, Area } from '../../constants'

export type AreaItem = { label: string; value: Area }

const translateNamespace = 'hooks.useArea'

// Sentinel value used by Select/Radio based area pickers to mean "all areas".
export const ALL_AREA = 'all'

/**
 * Consolidates everything area related into a single hook.
 * Area values live in `AREA_VALUES` (constants.ts) and labels live under the
 * `hooks.useArea` translation key, so adding/removing an area only requires
 * touching those two places.
 */
export const useArea = () => {
  const { t } = useTranslation()

  // label, value のリスト
  const areas: AreaItem[] = useMemo(
    () =>
      AREA_VALUES.map((value) => ({
        label: t(`${translateNamespace}.${value}`),
        value,
      })),
    [t]
  )

  // value だけの list
  const areaValues = useMemo<Area[]>(() => [...AREA_VALUES], [])

  // { [value]: label } のマップ
  const getAreaMap = useMemo(
    () => (): Record<Area, string> =>
      areas.reduce(
        (acc, { label, value }) => ({ ...acc, [value]: label }),
        {} as Record<Area, string>
      ),
    [areas]
  )

  // Select 用の { value, display } リスト。includeAll で先頭に「全てのエリア」を付与
  const getAreaOptions = useMemo(
    () =>
      ({ includeAll = false }: { includeAll?: boolean } = {}) => {
        const options = areas.map(({ label, value }) => ({
          value: value as string,
          display: label,
        }))
        if (includeAll) {
          return [
            { value: ALL_AREA, display: t(`${translateNamespace}.allAreas`) },
            ...options,
          ]
        }
        return options
      },
    [areas, t]
  )

  // 全エリア選択判定。'all' を含む、または全 value を包含していれば true
  const isAllAreas = useMemo(
    () => (values: string[]) =>
      values.includes(ALL_AREA) ||
      AREA_VALUES.every((value) => values.includes(value)),
    []
  )

  // 表示用文字列。全エリアなら「全てのエリア」、それ以外は label を ', ' 連結
  const getAreaLabel = useMemo(
    () => (values: string[]) => {
      if (isAllAreas(values)) {
        return t(`${translateNamespace}.allAreas`)
      }
      return areas
        .filter(({ value }) => values.includes(value))
        .map(({ label }) => label)
        .join(', ')
    },
    [areas, isAllAreas, t]
  )

  return {
    areas,
    areaValues,
    getAreaMap,
    getAreaOptions,
    isAllAreas,
    getAreaLabel,
  }
}
